import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface PhotoViewerProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export function PhotoViewer({ images, initialIndex = 0, isOpen, onClose }: PhotoViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!images || images.length === 0) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2000] bg-black/98 backdrop-blur-3xl flex flex-col items-center justify-center p-0 select-none"
          onClick={onClose}
        >
          {/* BOTÓN CERRAR (SOPORTA BOTÓN SEGURO NATIVO CON COBERTURA MÁXIMA) */}
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-3 bg-black/50 border border-white/10 rounded-full text-white hover:bg-black/70 active:scale-95 transition-all z-50 flex items-center justify-center shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>

          {/* INDICADOR DE PÁGINAS */}
          {images.length > 1 && (
            <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-black/50 border border-white/10 px-4 py-1.5 rounded-full backdrop-blur-md z-50 flex items-center justify-center shadow-lg">
              <span className="text-[10px] font-black text-white tracking-[3px] uppercase">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          )}

          {/* CONTENEDOR DE IMAGEN COMPLETAMENTE INMERSIVO (PANTALLA COMPLETA) */}
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full h-full max-w-full max-h-screen flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={images[currentIndex]} 
              alt={`Foto ${currentIndex + 1}`}
              className="w-full h-full max-h-screen object-contain pointer-events-none"
            />

            {/* NAVEGACIÓN LATERAL ULTRA-PREMIUM */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={handlePrev}
                  className="absolute left-4 p-3 bg-black/60 border border-white/5 rounded-full text-white hover:bg-black/90 active:scale-90 transition-all shadow-2xl flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleNext}
                  className="absolute right-4 p-3 bg-black/60 border border-white/5 rounded-full text-white hover:bg-black/90 active:scale-90 transition-all shadow-2xl flex items-center justify-center"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
